import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  ConfirmDialog,
  ResultDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { usdcTradesApi } from "@/services/api";
import {
  ArrowLeftRight,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  ExternalLink,
  MoreHorizontal,
  Eye,
  Check,
  ArrowUpRight,
  ArrowDownLeft,
  Zap,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface UsdcTrade {
  id: string;
  currencyConversionId: string | null;
  companyId: string;
  companyName?: string;
  cainvestTradeId?: string;
  cainvestQuoteId?: string;
  providerCode?: string;
  providerTradeId?: string;
  providerQuoteId?: string;
  providerStatus?: string;
  stablecoin: string;
  type: "BUY" | "SELL";
  brlAmount: number;
  usdcAmount: number;
  rate: string;
  status: string;
  cainvestStatus: string | null;
  settledAt: string | null;
  completedAt: string | null;
  reconciledAt: string | null;
  reconciliationStatus: string | null;
  reconciliationNotes: string | null;
  burnTxHash: string | null;
  mintTxHash: string | null;
  retryCount: number;
  lastCheckAt: string | null;
  nextCheckAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

const PROVIDER_DISPLAY: Record<string, { label: string; color: string }> = {
  cainvest: { label: "CAInvest", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  foxbit: { label: "Foxbit", color: "bg-orange-500/10 text-orange-600 border-orange-500/20" },
  ripple: { label: "Ripple", color: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  mock: { label: "Mock", color: "bg-gray-500/10 text-gray-600 border-gray-500/20" },
};

interface Stats {
  total: number;
  pending: number;
  processing: number;
  settled: number;
  completed: number;
  failed: number;
  expired: number;
  todayBrlVolume: number;
  todayBrlVolumeFormatted: string;
  todayUsdcVolume: number;
  todayUsdcVolumeFormatted: string;
  divergent: number;
}

const STATUS_CONFIG: Record<string, {
  label: string;
  variant: "secondary" | "warning" | "success" | "destructive";
  icon: typeof Clock;
  description: string;
}> = {
  PENDING: {
    label: "Pendente",
    variant: "secondary",
    icon: Clock,
    description: "Aguardando processamento no provedor",
  },
  PROCESSING: {
    label: "Processando",
    variant: "warning",
    icon: Loader2,
    description: "Em processamento no provedor",
  },
  SETTLED: {
    label: "Liquidada",
    variant: "warning",
    icon: Check,
    description: "Liquidada no provedor, aguardando reconciliação",
  },
  COMPLETED: {
    label: "Concluída",
    variant: "success",
    icon: CheckCircle,
    description: "Trade concluída e reconciliada",
  },
  FAILED: {
    label: "Falhou",
    variant: "destructive",
    icon: XCircle,
    description: "Erro no processamento",
  },
  EXPIRED: {
    label: "Expirada",
    variant: "secondary",
    icon: Clock,
    description: "Cotação expirou",
  },
};

const RECONCILIATION_STATUS_CONFIG: Record<string, {
  label: string;
  variant: "secondary" | "warning" | "success" | "destructive";
}> = {
  PENDING: { label: "Pendente", variant: "secondary" },
  OK: { label: "OK", variant: "success" },
  DIVERGENT: { label: "Divergente", variant: "destructive" },
  MANUAL_REVIEW: { label: "Revisão Manual", variant: "warning" },
};

export function UsdcTradesPage() {
  const [trades, setTrades] = useState<UsdcTrade[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [providerFilter, setProviderFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedTrade, setSelectedTrade] = useState<UsdcTrade | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    loading: boolean;
    confirmText?: string;
    variant?: "default" | "danger";
  }>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
    loading: false,
  });

  // Result dialog
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "success",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, [statusFilter, typeFilter, providerFilter, page]);

  async function loadData() {
    setLoading(true);
    try {
      const [tradesData, statsData] = await Promise.all([
        usdcTradesApi.list({
          status: statusFilter || undefined,
          type: typeFilter || undefined,
          providerCode: providerFilter || undefined,
          page,
          limit: 20,
        }),
        usdcTradesApi.getStats(),
      ]);
      // Backend retorna "items" no DTO ListUsdcTradesResponseDto
      setTrades(tradesData?.items || []);
      setTotal(tradesData?.total || 0);
      setTotalPages(tradesData?.totalPages || 1);
      setStats(statsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSyncTrade(id: string) {
    setSyncing(true);
    try {
      const result = await usdcTradesApi.syncTrade(id);
      setResultDialog({
        open: true,
        variant: result.error ? "error" : "success",
        title: result.error ? "Erro na sincronização" : "Trade sincronizada",
        description: result.error
          ? result.error
          : `Status: ${result.previousStatus || "?"} -> ${result.newStatus || "?"}`,
      });
      await loadData();
      if (selectedTrade?.id === id) {
        const updated = trades.find(t => t.id === id);
        if (updated) setSelectedTrade(updated);
      }
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao sincronizar",
        description: "Não foi possível sincronizar a trade. Tente novamente.",
      });
    } finally {
      setSyncing(false);
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true);
    try {
      const result = await usdcTradesApi.syncAll();
      setResultDialog({
        open: true,
        variant: result.failed > 0 ? "error" : "success",
        title: result.failed > 0 ? "Sincronização parcial" : "Sincronização concluída",
        description: `Processadas: ${result.totalProcessed}, Atualizadas: ${result.updated}, Completadas: ${result.completed}, Falhas: ${result.failed}`,
      });
      await loadData();
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao sincronizar",
        description: "Não foi possível sincronizar as trades. Tente novamente.",
      });
    } finally {
      setSyncingAll(false);
    }
  }

  async function handleComplete(id: string) {
    setConfirmDialog({
      open: true,
      title: "Completar Trade",
      description: "Deseja marcar esta trade como concluída manualmente? Use apenas se a trade já foi liquidada no provedor.",
      loading: false,
      confirmText: "Completar",
      variant: "default",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await usdcTradesApi.complete(id);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "success",
            title: "Trade completada",
            description: "A trade foi marcada como concluída.",
          });
          await loadData();
        } catch (error) {
          console.error("Erro ao completar:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao completar",
            description: "Não foi possível completar a trade. Tente novamente.",
          });
        }
      },
    });
  }

  async function handleFail(id: string) {
    setConfirmDialog({
      open: true,
      title: "Marcar como Falha",
      description: "Deseja marcar esta trade como falha? Isso irá cancelar a operação.",
      loading: false,
      confirmText: "Marcar Falha",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await usdcTradesApi.fail(id, "Marcada como falha pelo administrador");
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "success",
            title: "Trade marcada como falha",
            description: "A trade foi marcada como falha.",
          });
          await loadData();
        } catch (error) {
          console.error("Erro ao marcar falha:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao marcar falha",
            description: "Não foi possível marcar a trade como falha. Tente novamente.",
          });
        }
      },
    });
  }

  function getStatusBadge(status: string) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === "PROCESSING" ? "animate-spin" : ""}`} />
        {config.label}
      </Badge>
    );
  }

  function getReconciliationBadge(status: string | null) {
    if (!status) return null;
    const config = RECONCILIATION_STATUS_CONFIG[status] || RECONCILIATION_STATUS_CONFIG.PENDING;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  }

  function getTypeBadge(type: "BUY" | "SELL") {
    if (type === "BUY") {
      return (
        <Badge variant="secondary" className="flex items-center gap-1 bg-green-500/10 text-green-600 border-green-500/20">
          <ArrowDownLeft className="w-3 h-3" />
          Compra
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1 bg-red-500/10 text-red-600 border-red-500/20">
        <ArrowUpRight className="w-3 h-3" />
        Venda
      </Badge>
    );
  }

  function getProviderBadge(code?: string) {
    if (!code) return <Badge variant="secondary" className="text-xs">N/A</Badge>;
    const config = PROVIDER_DISPLAY[code];
    if (!config) return <Badge variant="secondary" className="text-xs">{code}</Badge>;
    return <Badge variant="secondary" className={`text-xs ${config.color}`}>{config.label}</Badge>;
  }

  if (loading && trades.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<ArrowLeftRight className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Trades USD"
          description="Gerenciamento de trades USD com provedores de liquidez"
        />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Operações USDC"
        description="Gerenciamento de trades USDC com CAInvest"
        stats={
          stats
            ? [
                { value: stats.total, label: "Total" },
                { value: stats.completed, label: "Concluídas" },
                { value: stats.pending + stats.processing + stats.settled, label: "Pendentes" },
                { value: stats.todayBrlVolumeFormatted, label: "Volume Hoje" },
              ]
            : []
        }
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={handleSyncAll}
              disabled={syncingAll}
            >
              <Zap className={`w-4 h-4 mr-2 ${syncingAll ? "animate-pulse" : ""}`} />
              Sincronizar Todas
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter(""); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold">{stats?.total || 0}</p>
            <p className="text-sm text-muted-foreground">Todas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "PENDING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("PENDING"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-gray-500">{stats?.pending || 0}</p>
            <p className="text-sm text-muted-foreground">Pendentes</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "PROCESSING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("PROCESSING"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-blue-500">{stats?.processing || 0}</p>
            <p className="text-sm text-muted-foreground">Processando</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "SETTLED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("SETTLED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-yellow-500">{stats?.settled || 0}</p>
            <p className="text-sm text-muted-foreground">Liquidadas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "COMPLETED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("COMPLETED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-green-500">{stats?.completed || 0}</p>
            <p className="text-sm text-muted-foreground">Concluídas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "FAILED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("FAILED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-red-500">{stats?.failed || 0}</p>
            <p className="text-sm text-muted-foreground">Falhas</p>
          </CardContent>
        </Card>
      </div>

      {/* Type + Provider Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={typeFilter === "" ? "default" : "outline"}
          size="sm"
          onClick={() => { setTypeFilter(""); setPage(1); }}
        >
          Todas
        </Button>
        <Button
          variant={typeFilter === "BUY" ? "default" : "outline"}
          size="sm"
          onClick={() => { setTypeFilter("BUY"); setPage(1); }}
        >
          <ArrowDownLeft className="w-4 h-4 mr-1" />
          Compra (BRLD→USD)
        </Button>
        <Button
          variant={typeFilter === "SELL" ? "default" : "outline"}
          size="sm"
          onClick={() => { setTypeFilter("SELL"); setPage(1); }}
        >
          <ArrowUpRight className="w-4 h-4 mr-1" />
          Venda (USD→BRLD)
        </Button>

        <div className="border-l border-border mx-1" />

        <select
          className="rounded-md border border-input bg-background px-3 py-1 text-sm"
          value={providerFilter}
          onChange={(e) => { setProviderFilter(e.target.value); setPage(1); }}
        >
          <option value="">Todos Provedores</option>
          <option value="cainvest">CAInvest</option>
          <option value="foxbit">Foxbit</option>
          <option value="mock">Mock</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trades Table */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Trades {statusFilter && `(${STATUS_CONFIG[statusFilter]?.label})`}
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {trades.length} de {total}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {trades.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma trade encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-visible">
                <table className="w-full">
                  <thead className="border-b border-border bg-muted/30">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="p-4 font-medium">Data</th>
                      <th className="p-4 font-medium">Tipo</th>
                      <th className="p-4 font-medium">Provedor</th>
                      <th className="p-4 font-medium">Valores</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map((trade) => (
                      <tr
                        key={trade.id}
                        className={`border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer ${
                          selectedTrade?.id === trade.id ? "bg-muted/30" : ""
                        }`}
                        onClick={() => setSelectedTrade(trade)}
                      >
                        <td className="p-4">
                          <p className="font-medium">{formatDate(trade.createdAt)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(trade.createdAt).toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </td>
                        <td className="p-4">
                          {getTypeBadge(trade.type)}
                        </td>
                        <td className="p-4">
                          {getProviderBadge(trade.providerCode)}
                        </td>
                        <td className="p-4">
                          <p className="font-semibold">
                            {formatCurrency(trade.brlAmount / 100)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            $ {(trade.usdcAmount / 100).toFixed(2)} USDC
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Taxa: {parseFloat(trade.rate).toFixed(4)}
                          </p>
                        </td>
                        <td className="p-4">
                          <div className="space-y-1">
                            {getStatusBadge(trade.status)}
                            {trade.reconciliationStatus && getReconciliationBadge(trade.reconciliationStatus)}
                          </div>
                        </td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSelectedTrade(trade)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver detalhes
                              </DropdownMenuItem>
                              {["PENDING", "PROCESSING", "SETTLED"].includes(trade.status) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSyncTrade(trade.id);
                                  }}
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Sincronizar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {trade.status === "SETTLED" && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleComplete(trade.id);
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Completar Manual
                                </DropdownMenuItem>
                              )}
                              {["PENDING", "PROCESSING"].includes(trade.status) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFail(trade.id);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Marcar Falha
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalhes da Trade</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedTrade ? (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  {getStatusBadge(selectedTrade.status)}
                  <span className="text-xs text-muted-foreground">
                    {selectedTrade.id.slice(0, 8)}...
                  </span>
                </div>

                {/* Tipo */}
                <div className="text-center">
                  {getTypeBadge(selectedTrade.type)}
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedTrade.type === "BUY" ? "BRLD → USDC" : "USDC → BRLD"}
                  </p>
                </div>

                {/* Valores */}
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">BRL</span>
                    <span className="font-bold">{formatCurrency(selectedTrade.brlAmount / 100)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">USDC</span>
                    <span className="font-bold">$ {(selectedTrade.usdcAmount / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-border pt-2">
                    <span className="text-muted-foreground">Taxa</span>
                    <span>{parseFloat(selectedTrade.rate).toFixed(6)}</span>
                  </div>
                </div>

                {/* Provedor */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Provedor</p>
                  <div className="flex items-center gap-2 mb-2">
                    {getProviderBadge(selectedTrade.providerCode)}
                  </div>
                  <div className="text-xs space-y-1">
                    <p><span className="text-muted-foreground">Trade ID:</span> {selectedTrade.providerTradeId || selectedTrade.cainvestTradeId || "-"}</p>
                    <p><span className="text-muted-foreground">Quote ID:</span> {selectedTrade.providerQuoteId || selectedTrade.cainvestQuoteId || "-"}</p>
                  </div>
                </div>

                {/* Status do Provedor */}
                {(selectedTrade.providerStatus || selectedTrade.cainvestStatus) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Status no Provedor</p>
                    <Badge variant="secondary">{selectedTrade.providerStatus || selectedTrade.cainvestStatus}</Badge>
                  </div>
                )}

                {/* Reconciliação */}
                {selectedTrade.reconciliationStatus && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Reconciliação</p>
                    {getReconciliationBadge(selectedTrade.reconciliationStatus)}
                    {selectedTrade.reconciliationNotes && (
                      <p className="text-xs text-muted-foreground mt-1">{selectedTrade.reconciliationNotes}</p>
                    )}
                  </div>
                )}

                {/* Timeline */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Timeline</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Criada</span>
                      <span>{formatDate(selectedTrade.createdAt)}</span>
                    </div>
                    {selectedTrade.settledAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Liquidada</span>
                        <span>{formatDate(selectedTrade.settledAt)}</span>
                      </div>
                    )}
                    {selectedTrade.completedAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Concluída</span>
                        <span className="text-green-600">{formatDate(selectedTrade.completedAt)}</span>
                      </div>
                    )}
                    {selectedTrade.lastCheckAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Última Verificação</span>
                        <span>{formatDate(selectedTrade.lastCheckAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Blockchain Transactions */}
                {(selectedTrade.burnTxHash || selectedTrade.mintTxHash) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Transações Blockchain</p>
                    <div className="space-y-1">
                      {selectedTrade.burnTxHash && (
                        <a
                          href={`https://explorer.xinfin.network/tx/${selectedTrade.burnTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <span>Burn: {selectedTrade.burnTxHash.slice(0, 12)}...</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {selectedTrade.mintTxHash && (
                        <a
                          href={`https://explorer.xinfin.network/tx/${selectedTrade.mintTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <span>Mint: {selectedTrade.mintTxHash.slice(0, 12)}...</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Retry Info */}
                {selectedTrade.retryCount > 0 && (
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                    <p className="text-sm font-medium text-warning mb-1">Tentativas</p>
                    <p className="text-xs">{selectedTrade.retryCount} tentativa(s)</p>
                  </div>
                )}

                {/* Error Message */}
                {selectedTrade.errorMessage && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                    <p className="text-sm font-medium text-destructive mb-1">Erro</p>
                    <p className="text-xs text-destructive">{selectedTrade.errorMessage}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {["PENDING", "PROCESSING", "SETTLED"].includes(selectedTrade.status) && (
                    <Button
                      className="flex-1"
                      variant="outline"
                      onClick={() => handleSyncTrade(selectedTrade.id)}
                      disabled={syncing}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                      Sincronizar
                    </Button>
                  )}
                  {selectedTrade.status === "SETTLED" && (
                    <Button
                      className="flex-1"
                      onClick={() => handleComplete(selectedTrade.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Completar
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">Selecione uma trade para ver os detalhes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText || "Confirmar"}
        variant={confirmDialog.variant || "default"}
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
